/**
 * Where handled errors go. Rollbar's `captureUncaught`/`captureUnhandledRejections` never
 * see anything we *catch*, so those used to only go to `console.error` — visible only in the
 * tab it happened in, and only with devtools open. The console line stays in development.
 *
 * The reporter is **registered**, not constructed here: its config comes from
 * `window._env_`, generated by Docker and empty when run directly on the host, and reading
 * it at import time would throw in this module's unit-test consumers. So it stays inert
 * until `App` hands it an instance — safe (including in tests) before that.
 */

/**
 * Structurally what Rollbar exposes, narrowed to the two levels used here. `string | Error` is a
 * subset of Rollbar's own `LogArgument`, so its instance satisfies this without a cast — which
 * is the point: if the SDK's signature changes, this stops compiling instead of silently
 * swallowing reports.
 */
type ReportArgument = string | Error

interface ErrorReporter {
  error: (...args: ReportArgument[]) => unknown
  warning: (...args: ReportArgument[]) => unknown
}

/** Rollbar renders an `Error` with its stack; anything else is more useful as text. */
function reportable(value: unknown): ReportArgument {
  if (value instanceof Error) return value
  try {
    return typeof value === 'string' ? value : JSON.stringify(value)
  } catch {
    return String(value)
  }
}

let reporter: ErrorReporter | null = null

/** Called once by `App`, with the same Rollbar instance the React provider uses. */
export function registerErrorReporter(instance: ErrorReporter | null): void {
  reporter = instance
}

/**
 * @param context where it happened, e.g. `'document: save'`. Becomes the Rollbar title, so make
 *   it stable and greppable rather than interpolating ids into it.
 */
export function logError(context: string, error: unknown): void {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.error(`[${context}]`, error)
  }

  // Never let reporting be the thing that breaks the handler that called it.
  try {
    reporter?.error(context, reportable(error))
  } catch {
    /* reporting is best-effort */
  }
}

/**
 * For a condition that is not an error but means something silently did not happen — a feature
 * whose command the shipped editor build lacks, AI changes dropped because their text moved.
 * These are exactly the failures nobody reports, because nothing looked broken.
 */
export function logWarning(context: string, detail?: unknown): void {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.warn(`[${context}]`, detail)
  }

  try {
    reporter?.warning(context, reportable(detail))
  } catch {
    /* reporting is best-effort */
  }
}

export default logError
